import React from 'react';
import { Stack } from '@mui/material';
import { grey } from '@mui/material/colors';
import { BpfLinkType } from '@trnsact/trnsact-shared-types';

import { AttributeRenderers } from './types';
import { Checklist, Document } from './attributesComponents';
import { Stage, UpdateStageFields, useBPFStages } from '../../api';

interface Props {
  stage: Stage;
  parentId: string;
  isLoading: boolean;
  parentType: BpfLinkType;
  onUpdateStageAttributes: (dto: UpdateStageFields) => Promise<void>;
}

export const StageAttributes = ({ parentId, parentType, stage, isLoading, onUpdateStageAttributes }: Props) => {
  const { handleUploadStageDocument, isDocumentUploading, handleRemoveStageDocument, isDocumentRemoving } =
    useBPFStages();

  const stageFields = stage.fieldValues?.values?.length ? stage.fieldValues.values : stage.stageFields;

  const attributeRenderers: AttributeRenderers = {
    checklist: attribute => (
      <Checklist
        attribute={attribute}
        isLoading={isLoading}
        onChange={async (checklistItem, nextValue) => {
          await onUpdateStageAttributes({
            parentId,
            parentType,
            stageId: stage.businessProcessFlowStageId,
            stageFields: stageFields.map(field => {
              if (field.field === 'checklist') {
                return {
                  ...field,
                  value: field.value.map(fieldValue => {
                    if (fieldValue.name === checklistItem.name) {
                      return { ...fieldValue, value: nextValue };
                    }

                    return fieldValue;
                  }),
                };
              }

              return field;
            }),
          });
        }}
      />
    ),
    document: attribute => (
      <Document
        parentId={parentId}
        attribute={attribute}
        parentType={parentType}
        isLoading={isDocumentUploading}
        isDeleting={isDocumentRemoving}
        stageId={stage.businessProcessFlowStageId}
        onDelete={id => {
          handleRemoveStageDocument(id, api => ({
            refetchQueries: [
              api.getStageAttributeDocuments,
              api.fetchVOTemplatesAndStages,
              api.fetchCSTemplatesAndStages,
            ],
            awaitRefetchQueries: true,
          }));
        }}
        onUpload={async (files: File[], attributeItem) => {
          await handleUploadStageDocument(
            {
              files,
              parentId,
              parentType,
              isPii: attributeItem.isPII,
              docName: attributeItem.name,
              attributeId: attributeItem.id,
              stageId: stage.businessProcessFlowStageId,
              availableForRoles: attributeItem.availableForRoles,
            },
            api => ({
              refetchQueries: [
                api.getStageAttributeDocuments,
                api.fetchVOTemplatesAndStages,
                api.fetchCSTemplatesAndStages,
              ],
              awaitRefetchQueries: true,
            })
          );
        }}
      />
    ),
  };

  return (
    <Stack gap="0.2rem" width="100%">
      {stageFields.map((attribute, index) => (
        <Stack
          width="100%"
          direction="row"
          key={`${attribute.field}_${index}`}
          sx={{ borderBottom: `1px solid ${grey[200]}`, minHeight: '50px', alignItems: 'center' }}
        >
          {attributeRenderers[attribute.field] && attributeRenderers[attribute.field](attribute as any)}
        </Stack>
      ))}
    </Stack>
  );
};
